/**
 * Real Backend API Endpoints
 * These connect to your actual backend at 192.168.1.214:3001
 */

import {
  apiClient,
  AUTH_HOST,
  USER_API_URL,
  COMMON_API_URL,
  BOOKING_API_URL,
  PAYMENT_API_URL,
} from './client';
import type { RealProvidersResponse } from './realTypes';
import type { LoginRequest, LoginResponse, MetricsResponse } from './types';
import { handleApiError } from '../utils/apiErrorHandler';
import { checkInvalidToken } from '../utils/authGuard';

export const realAPI = {
  // Transactions endpoint
  transactions: {
    getList: async (
      limit: number = 10,
      offset: number = 0,
      paymentStatus?: string,
      dateFrom?: string,
      dateTo?: string,
      shortBy?: string,
      shortType?: string
    ) => {
      const token = `Bearer ${localStorage.getItem('authToken') || ''}`;

      const params = new URLSearchParams({
        limit: String(limit),
        offset: String(offset),
      });

      if (shortBy) {
        params.append('shortBy', shortBy);
        if (shortType) {
          params.append('shortType', shortType);
        }
      }

      if (paymentStatus) {
        params.append('paymentStatus', paymentStatus);
      }

      if (dateFrom) {
        params.append('startDate', dateFrom);
      }

      if (dateTo) {
        params.append('endDate', dateTo);
      }

      const url = `${PAYMENT_API_URL}transactions?${params.toString()}`;

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'text/plain', // Using text/plain as per user curl, though json is standard.
          Authorization: token,
        },
      });

      if (checkInvalidToken(response)) return;

      if (!response.ok) {
        // Handle specific 404 "nothing here" case
        if (response.status === 404) {
          try {
            const errorData = await response.json();
            if (
              errorData.errorMessage === "Sorry, there's nothing here" ||
              errorData.error === 'notFound'
            ) {
              return []; // Return empty array so UI shows no records
            }
          } catch (e) {
            // If json parse fails, fall through to generic error
          }
        }
        throw new Error('Failed to fetch transactions');
      }

      const data = await response.json();
      return data;
    },

    getDetails: async (id: string | number) => {
      const token = `Bearer ${localStorage.getItem('authToken') || ''}`;
      const url = `${PAYMENT_API_URL}transactions/${id}`;

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          Authorization: token,
        },
      });

      if (checkInvalidToken(response)) return;

      if (!response.ok) {
        throw new Error('Failed to fetch transaction details');
      }

      const data = await response.json();
      return data;
    },
  },

  // Auth endpoints (still using mock for now)
  auth: {
    login: (data: LoginRequest) => apiClient.post<LoginResponse>('/api/auth/login', data),
    forgotPassword: async (email: string) => {
      const url = `${AUTH_HOST}forget-password`;

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });

      if (response.status === 404) {
        // const error = new Error("Email not registered");
        // (error as any).status = 404;
        throw response;
      }

      if (!response.ok) {
        throw new Error('Failed to send reset email');
      }

      const data = await response.json();
      return data;
    },
  },

  // Real users (customers) endpoint
  users: {
    getList: async (
      page: number = 1,
      perPage: number = 20,
      search?: string,
      status?: string,
      shortBy?: string,
      shortType?: string,
      isDeleted?: boolean
    ) => {
      const token = `Bearer ${localStorage.getItem('authToken') || ''}`;
      const limit = perPage;
      const offset = (page - 1) * perPage;

      const params = new URLSearchParams({
        limit: String(limit),
        offset: String(offset),
      });

      if (search) {
        params.append('search', search);
      }

      if (status) {
        params.append('status', status);
      }

      if (shortBy) {
        params.append('shortBy', shortBy);
      }

      if (shortType) {
        params.append('shortType', shortType);
      }

      if (isDeleted) {
        params.append('isDeleted', 'yes');
      }

      const url = `${USER_API_URL}users-list?${params.toString()}`;

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: token,
        },
      });

      if (checkInvalidToken(response)) return;
      if (!response.ok) {
        return {
          results: [],
          count: 0,
        };
      }

      const data = await response.json();
      return {
        results: data.results,
        count: data.count,
        deletedCount: data.deletedCount || 0, // Include deleted count if available
      };
    },

    getProfile: async () => {
      const token = `Bearer ${localStorage.getItem('authToken') || ''}`;
      const url = `${USER_API_URL}profile`;

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: token,
        },
      });

      if (checkInvalidToken(response)) return;
      if (!response.ok) {
        throw new Error('Failed to fetch profile');
      }

      const data = await response.json();
      return data.result; // Extract the result object from the response
    },

    updateUser: async (
      id: string | number,
      updates: { status?: string; name?: string; email?: string; username?: string; image?: string }
    ) => {
      const token = `Bearer ${localStorage.getItem('authToken') || ''}`;

      const response = await fetch(`${USER_API_URL}update-profile`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: token,
        },
        body: JSON.stringify({
          id: String(id),
          ...updates, // Include status, name, or email updates
        }),
      });
      if (checkInvalidToken(response)) return;
      if (!response.ok) {
        if (response.status === 409) {
          throw new Error('Email already exists');
        }
        throw new Error('Failed to update user');
      }

      const data = await response.json();
      return data;
    },

    // Get user details by ID
    getUserDetails: async (id: string | number) => {
      const token = `Bearer ${localStorage.getItem('authToken') || ''}`;

      const response = await fetch(`${USER_API_URL}details/${id}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: token,
        },
      });
      if (checkInvalidToken(response)) return;
      if (!response.ok) {
        throw new Error('Failed to fetch user details');
      }

      const data = await response.json();
      // Handle nested result structure
      return data.result || data;
    },

    // Delete user account
    deleteUser: async (id: string | number) => {
      const token = `Bearer ${localStorage.getItem('authToken') || ''}`;

      const response = await fetch(`${USER_API_URL}account-delete/${id}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          Authorization: token,
        },
      });
      if (checkInvalidToken(response)) return;

      if (!response.ok) {
        throw new Error('Failed to delete user account');
      }

      const data = await response.json();
      return data;
    },

    // Restore user/provider account from deleted list
    restoreUser: async (id: string | number) => {
      const token = `Bearer ${localStorage.getItem('authToken') || ''}`;

      const response = await fetch(`${USER_API_URL}account-restore/${id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: token,
        },
      });
      if (checkInvalidToken(response)) return;

      if (!response.ok) {
        throw new Error('Failed to restore user account');
      }

      const data = await response.json();
      return data;
    },

    // Export customers as CSV
    exportCustomers: async () => {
      const token = `Bearer ${localStorage.getItem('authToken') || ''}`;
      const url = `${USER_API_URL}export-users?userType=customer`;

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          Authorization: token,
        },
      });

      if (checkInvalidToken(response)) return;
      if (!response.ok) {
        throw new Error('Failed to export customers');
      }

      return await response.blob();
    },
  },

  // Real providers endpoint
  providers: {
    getList: async (
      page: number = 1,
      perPage: number = 20,
      search?: string,
      status?: string,
      shortBy?: string,
      shortType?: string,
      includeRatings: boolean = true,
      isDeleted?: boolean
    ) => {
      const token = `Bearer ${localStorage.getItem('authToken') || ''}`;

      const limit = perPage;
      const offset = (page - 1) * perPage;

      const params = new URLSearchParams({
        limit: String(limit),
        offset: String(offset),
      });

      if (search) {
        params.append('search', search);
      }

      if (status) {
        params.append('status', status);
      }

      if (shortBy) {
        params.append('shortBy', shortBy);
      }

      if (shortType) {
        params.append('shortType', shortType);
      }

      // Request ratings data to be included
      if (includeRatings) {
        params.append('includeRatings', 'true');
      }

      if (isDeleted) {
        params.append('isDeleted', 'yes');
      }

      const url = `${USER_API_URL}providers-list?${params.toString()}`;

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: token,
        },
      });
      if (checkInvalidToken(response)) return;

      if (!response.ok) {
        return {
          results: [],
          count: 0,
          total: 0,
          deletedCount: 0,
        };
      }

      const data: RealProvidersResponse = await response.json();
      return {
        count: data.count, // total records in server
        results: data.results, // paginated results from server
        total: data.count, // same as count
        deletedCount: data.deletedCount || 0,
      };
    },

    updateProvider: async (
      id: string | number,
      updates: {
        status?: 'active' | 'inactive';
        verified?: boolean;
        isVerified?: string;
        isFeatured?: string;
      }
    ) => {
      const token = `Bearer ${localStorage.getItem('authToken') || ''}`;
      // Handle status update
      if (updates.status) {
        const response = await fetch(`${USER_API_URL}update-profile`, {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            Authorization: token,
          },
          body: JSON.stringify({
            status: updates.status,
            id: String(id),
          }),
        });
        if (checkInvalidToken(response)) return;

        if (!response.ok) {
          throw new Error('Failed to update provider');
        }

        const data = await response.json();
        return data;
      }

      // Handle verification update
      if (updates.isVerified !== undefined) {
        const response = await fetch(`${USER_API_URL}update-profile`, {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            Authorization: token,
          },
          body: JSON.stringify({
            isVerified: updates.isVerified,
            id: String(id),
          }),
        });

        if (checkInvalidToken(response)) return;
        if (!response.ok) {
          throw new Error('Failed to update provider verification');
        }

        const data = await response.json();
        return data;
      }

      // Handle legacy verified boolean update
      if (updates.verified !== undefined) {
        const response = await fetch(`${USER_API_URL}update-profile`, {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            Authorization: token,
          },
          body: JSON.stringify({
            verified: updates.verified,
            id: String(id),
          }),
        });

        if (checkInvalidToken(response)) return;
        if (!response.ok) {
          throw new Error('Failed to update provider verification');
        }

        const data = await response.json();
        return data;
      }

      if (updates.isFeatured) {
        const response = await fetch(`${USER_API_URL}update-profile`, {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            Authorization: token,
          },
          body: JSON.stringify({
            isFeatured: updates.isFeatured,
            id: String(id),
          }),
        });
        if (checkInvalidToken(response)) return;

        if (!response.ok) {
          throw new Error('Failed to update provider');
        }

        const data = await response.json();
        return data;
      }

      return { success: true };
    },

    // Get provider details with pagination for ratings and services
    getProviderDetails: async (
      providerId: string | number,
      limit: number = 6,
      offset: number = 0
    ) => {
      const token = `Bearer ${localStorage.getItem('authToken') || ''}`;
      const response = await fetch(
        `${USER_API_URL}provider/${providerId}?limit=${limit}&offset=${offset}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: token,
          },
        }
      );
      if (checkInvalidToken(response)) return;

      if (!response.ok) {
        if (response.status === 401) {
          // Try to get error details for token verification
          try {
            const errorData = await response.json();
            handleApiError({ response: { status: response.status, data: errorData } });
          } catch {
            handleApiError({
              response: { status: response.status, data: { error: 'unauthorized' } },
            });
          }
        }
        throw new Error('Failed to fetch provider details');
      }

      const data = await response.json();
      return data;
    },

    // Get paginated services for a provider
    getProviderServices: async (providerId: string, page: number = 1, limit: number = 4) => {
      // Use the real provider details endpoint to get services
      const providerDetails = await realAPI.providers.getProviderDetails(providerId, 100, 0);
      const services = providerDetails.result.providerServices || [];

      const offset = (page - 1) * limit;
      const paginatedServices = services.slice(offset, offset + limit);

      return {
        data: paginatedServices,
        total: services.length,
        page,
        limit,
      };
    },

    // Export providers as CSV
    exportProviders: async () => {
      const token = `Bearer ${localStorage.getItem('authToken') || ''}`;
      const url = `${USER_API_URL}export-users?userType=provider`;
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          Authorization: token,
        },
      });

      if (checkInvalidToken(response)) return;

      if (!response.ok) {
        throw new Error('Failed to export providers');
      }

      return await response.blob();
    },
  },

  // Dashboard metrics endpoint
  dashboard: {
    getMetrics: async () => {
      const token = `Bearer ${localStorage.getItem('authToken') || ''}`;
      const response = await fetch(`${USER_API_URL}dashboard`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: token,
        },
      });
      if (checkInvalidToken(response)) return;

      if (!response.ok) {
        throw new Error('Failed to fetch dashboard metrics');
      }

      const data = await response.json();
      return {
        totalCustomers: data.totalUsers || 0,
        totalProviders: data.totalProviders || 0,
        totalServices: data.totalServices || 0,
        totalCategory: data.totalCategories || 0,
        subscribedProviders: 0,
        totalBooking: data.totalBooking,
        completedBookings: data.totalCompletedBooking,
        totalRevenue: data.totalRevenue,
      };
    },

    // Users graph endpoint
    getUsersGraph: async (
      filter: string = 'daily',
      userType: string = 'customer',
      fromDate?: string,
      toDate?: string
    ) => {
      const token = `Bearer ${localStorage.getItem('authToken') || ''}`;
      const params = new URLSearchParams({
        filter: filter,
      });

      if (fromDate) {
        params.append('fromDate', fromDate);
      }
      if (toDate) {
        params.append('toDate', toDate);
      }

      // The endpoint should be users-graph not users-graph? (remove the question mark)
      const url = `${USER_API_URL}users-graph?${params.toString()}`;

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: token,
        },
      });

      if (checkInvalidToken(response)) return;

      if (!response.ok) {
        console.error('API Error:', response.status, response.statusText);
        try {
          const errorData = await response.json();
          console.error('Error details:', errorData);
        } catch (e) {
          console.error('Could not parse error response');
        }
        return {
          graphData: [],
          filter: {
            type: filter,
            userType: userType,
            fromDate: fromDate || '',
            toDate: toDate || '',
          },
        };
      }

      try {
        const data = await response.json();
        return data;
      } catch (error) {
        console.error('Error parsing response:', error);
        return {
          graphData: [],
          filter: {
            type: filter,
            userType: userType,
            fromDate: fromDate || '',
            toDate: toDate || '',
          },
        };
      }
    },

    // Bookings graph endpoint
    getBookingsGraph: async (filter: string = 'daily', fromDate?: string, toDate?: string) => {
      const token = `Bearer ${localStorage.getItem('authToken') || ''}`;
      const params = new URLSearchParams({
        filter: filter,
      });

      if (fromDate) {
        params.append('fromDate', fromDate);
      }
      if (toDate) {
        params.append('toDate', toDate);
      }

      const url = `${BOOKING_API_URL}bookings-graph?${params.toString()}`;
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: token,
        },
      });

      if (checkInvalidToken(response)) return;

      if (!response.ok) {
        console.error('API Error:', response.status, response.statusText);
        return {
          graphData: [],
          filter: {
            type: filter,
            status: 'all',
            fromDate: fromDate || '',
            toDate: toDate || '',
          },
        };
      }

      try {
        const data = await response.json();
        return data;
      } catch (error) {
        console.error('Error parsing response:', error);
        return {
          graphData: [],
          filter: {
            type: filter,
            status: 'all',
            fromDate: fromDate || '',
            toDate: toDate || '',
          },
        };
      }
    },
  },

  // Metrics endpoint (still using mock for now)
  metrics: {
    getMetrics: (from?: string, to?: string) =>
      apiClient.get<MetricsResponse>('/api/metrics', { from: from || '', to: to || '' }),
  },

  // Categories endpoint
  categories: {
    getList: async (
      search: string = '',
      page: number = 1,
      limit: number = 10,
      status?: string,
      shortBy?: string,
      shortType?: string
    ) => {
      const token = `Bearer ${localStorage.getItem('authToken') || ''}`;
      const offset = (page - 1) * limit;
      const params = new URLSearchParams({
        limit: String(limit),
        offset: String(offset),
      });

      if (search) {
        params.append('search', search);
      }

      if (status && status !== 'all') {
        params.append('status', status);
      }

      if (shortBy) {
        params.append('shortBy', shortBy);
      }

      if (shortType) {
        params.append('shortType', shortType);
      }

      const url = `${COMMON_API_URL}admin/category?${params.toString()}`;

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: token,
        },
      });

      if (checkInvalidToken(response)) return;
      if (!response.ok) {
        const errorData = await response.json();
        if (errorData.error === 'not_found') {
          return {
            data: [],
            total: 0,
            error: 'not_found',
          };
        }
        throw new Error('Failed to fetch categories');
      }

      const data = await response.json();
      return {
        data: data.result.map((item: any) => ({
          id: String(item.id),
          name: item.name,
          description: item.description,
          status: item.status,
          image: item.image,
          parentCategoryName: item.parentCategoryName,
          createdAt: item.createdAt,
          updatedAt: item.createdAt,
        })),
        total: data.counts ?? 0,
      };
    },

    createCategory: async (categoryData: { name: string }) => {
      const token = `Bearer ${localStorage.getItem('authToken') || ''}`;
      const response = await fetch(`${COMMON_API_URL}category`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: token,
        },
        body: JSON.stringify(categoryData),
      });

      if (checkInvalidToken(response)) return;
      if (!response.ok) {
        throw new Error('Failed to create category');
      }

      return await response.json();
    },

    updateCategory: async (id: string, updates: { name?: string; status?: string }) => {
      const token = `Bearer ${localStorage.getItem('authToken') || ''}`;
      // For now, using the same endpoint - update if different endpoint exists
      const response = await fetch(`${COMMON_API_URL}category/${id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: token,
        },
        body: JSON.stringify(updates),
      });

      if (checkInvalidToken(response)) return;
      if (!response.ok) {
        throw new Error('Failed to update category');
      }
      return await response.json();
    },

    deleteCategory: async (id: string) => {
      const token = `Bearer ${localStorage.getItem('authToken') || ''}`;
      const response = await fetch(`${COMMON_API_URL}category/${id}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          Authorization: token,
        },
      });

      if (checkInvalidToken(response)) return;
      if (!response.ok) {
        throw new Error('Failed to delete category');
      }

      return await response.json();
    },
  },

  // Customers endpoint (still using mock for now)
  customers: {
    getCustomers: (page: number, perPage: number, search?: string) =>
      apiClient.get('/api/customers', {
        page,
        perPage,
        search: search || '',
      }),
    updateCustomer: (id: string, data: { status: 'active' | 'inactive' }) =>
      apiClient.patch(`/api/customers/${id}`, data),
  },

  // Bookings endpoint
  bookings: {
    getBookings: async (
      limit: number = 10,
      offset: number = 0,
      status?: string,
      dateFrom?: string,
      dateTo?: string,
      shortBy?: string,
      shortType?: string
    ) => {
      const token = `Bearer ${localStorage.getItem('authToken') || ''}`;

      let url = `${BOOKING_API_URL}admin-booking?limit=${limit}&offset=${offset}`;
      if (status) {
        url += `&status=${status}`;
      }
      // Send individual date parameters (match UsersAnalyticsGraph pattern)
      if (dateFrom) {
        url += `&startDate=${dateFrom}`;
      }
      if (dateTo) {
        url += `&endDate=${dateTo}`;
      }

      if (shortBy) {
        url += `&shortBy=${shortBy}`;
      }

      if (shortType) {
        url += `&shortType=${shortType}`;
      }

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: token,
        },
      });

      if (checkInvalidToken(response)) return;
      if (!response.ok) {
        console.error('API Error:', response.status, response.statusText);
        try {
          const errorData = await response.json();
          console.error('Error details:', errorData);
        } catch (e) {
          console.error('Could not parse error response');
        }
        return {
          results: [],
          count: 0,
        };
      }

      const data = await response.json();

      // Handle different response structures
      let results = [];
      let count = 0;

      // Direct structure: { results: [], counts: number }
      if (data && data.results && data.counts !== undefined) {
        results = data.results;
        count = data.counts;
      } else if (data && data.data) {
        // If data is nested under 'data' key
        results = data.data.bookings || data.data || [];
        count = data.data.total || data.data.count || data.data.counts || results.length;
      } else if (data && data.results) {
        // If data has 'results' key (original structure)
        results = data.results;
        count = data.count || data.counts || results.length;
      } else if (data && data.bookings) {
        // If data has 'bookings' key at root level
        results = data.bookings;
        count = data.count || data.total || data.counts || results.length;
      } else if (Array.isArray(data)) {
        // If data is directly an array
        results = data;
        count = data.length;
      } else {
        // Fallback - check if data itself has count and extract results
        results = data.results || data.bookings || data.data || [];
        count =
          data.count || data.total || data.counts || (Array.isArray(results) ? results.length : 0);
      }

      return {
        results: results,
        counts: count,
      };
    },

    getBookingDetails: async (id: string | number) => {
      const token = `Bearer ${localStorage.getItem('authToken') || ''}`;

      const response = await fetch(`${BOOKING_API_URL}booking/${id}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: token,
        },
      });

      if (checkInvalidToken(response)) return;
      if (!response.ok) {
        throw new Error('Failed to fetch booking details');
      }

      const data = await response.json();
      return data.result || data;
    },

    deleteBooking: async (id: string | number) => {
      const token = `Bearer ${localStorage.getItem('authToken') || ''}`;

      const response = await fetch(`${BOOKING_API_URL}booking/${id}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          Authorization: token,
        },
      });

      if (checkInvalidToken(response)) return;
      if (!response.ok) {
        throw new Error('Failed to delete booking');
      }

      const data = await response.json();
      return data;
    },
  },

  // Content Management endpoint
  content: {
    getContent: async () => {
      const token = `Bearer ${localStorage.getItem('authToken') || ''}`;

      const response = await fetch(`${COMMON_API_URL}content`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: token,
        },
      });

      if (checkInvalidToken(response)) return;
      if (!response.ok) {
        if (response.status === 404) {
          throw new Error('HTTP 404');
        }
        return {
          count: 0,
          result: [],
        };
      }

      const data = await response.json();
      return data;
    },

    updateContentStatus: async (id: string | number, status: 'active' | 'inactive') => {
      const token = `Bearer ${localStorage.getItem('authToken') || ''}`;

      const response = await fetch(`${COMMON_API_URL}admin-content/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: token,
        },
        body: JSON.stringify({
          status: status,
        }),
      });

      if (checkInvalidToken(response)) return;
      if (!response.ok) {
        throw new Error('Failed to update content status');
      }

      const data = await response.json();
      return data;
    },

    deleteContent: async (id: string | number) => {
      const token = `Bearer ${localStorage.getItem('authToken') || ''}`;

      const response = await fetch(`${COMMON_API_URL}admin-content/${id}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          Authorization: token,
        },
      });

      if (checkInvalidToken(response)) return;
      if (!response.ok) {
        throw new Error('Failed to delete content');
      }

      const data = await response.json();
      return data;
    },

    addContent: async (type: string, content: string[], name: string) => {
      const token = `Bearer ${localStorage.getItem('authToken') || ''}`;

      const response = await fetch(`${COMMON_API_URL}admin-content/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: token,
        },
        body: JSON.stringify({
          type: type,
          content: content,
          name: name,
        }),
      });

      if (checkInvalidToken(response)) return;
      if (!response.ok) {
        throw new Error('Failed to add content');
      }

      const data = await response.json();
      return data;
    },

    updateContent: async (id: string | number, type: string, content: string[], name: string) => {
      const token = `Bearer ${localStorage.getItem('authToken') || ''}`;

      const response = await fetch(`${COMMON_API_URL}admin-content/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: token,
        },
        body: JSON.stringify({
          type: type,
          content: content,
          name: name,
        }),
      });

      if (checkInvalidToken(response)) return;
      if (!response.ok) {
        throw new Error('Failed to update content');
      }

      const data = await response.json();
      return data;
    },
  },

  // Support/Contact Us endpoint
  support: {
    getContacts: async (
      limit: number = 10,
      offset: number = 0,
      search?: string,
      sortBy?: string,
      sortOrder?: string
    ) => {
      const token = `Bearer ${localStorage.getItem('authToken') || ''}`;

      const params = new URLSearchParams({
        limit: String(limit),
        offset: String(offset),
      });

      // if (search) {
      //   params += `'search', search);
      // }

      if (sortBy) {
        params.append('sortBy', sortBy);
      }

      if (sortOrder) {
        params.append('sortOrder', sortOrder);
      }

      const url = `${COMMON_API_URL}contact-us?${params.toString()}`;

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: token,
        },
      });

      if (checkInvalidToken(response)) return;
      if (!response.ok) {
        return {
          results: [],
          count: 0,
        };
      }

      const data = await response.json();
      return {
        results: data.results || [],
        count: data.count || 0,
      };
    },

    deleteContact: async (id: number) => {
      const token = `Bearer ${localStorage.getItem('authToken') || ''}`;

      const response = await fetch(`${COMMON_API_URL}contact-us/${id}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          Authorization: token,
        },
      });

      if (checkInvalidToken(response)) return;
      if (!response.ok) {
        throw new Error('Failed to delete contact');
      }

      const data = await response.json();
      return data;
    },
  },

  // Subscription plans (admin)
  providerPlans: {
    getProviderPlans: async (providerId: string | number) => {
      const token = `Bearer ${localStorage.getItem('authToken') || ''}`;
      const response = await fetch(`${PAYMENT_API_URL}pro-plans/${providerId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: token,
        },
      });

      if (checkInvalidToken(response)) return;
      if (response.status === 404) {
        return { results: [], counts: 0 };
      }
      if (!response.ok) {
        throw new Error('Failed to fetch provider plans');
      }
      return await response.json();
    },

    getProviderPlanDetails: async (planId: string | number) => {
      const token = `Bearer ${localStorage.getItem('authToken') || ''}`;
      const response = await fetch(`${PAYMENT_API_URL}pro-plans-details/${planId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: token,
        },
      });

      if (checkInvalidToken(response)) return;
      if (!response.ok) {
        throw new Error('Failed to fetch provider plan details');
      }

      return await response.json();
    },
  },

  // Admin subscription plans
  subscriptionPlans: {
    getPlans: async (
      limit: number = 10,
      offset: number = 0,
      deviceType?: string,
      type?: string
    ) => {
      const token = `Bearer ${localStorage.getItem('authToken') || ''}`;
      const params = new URLSearchParams({
        limit: String(limit),
        offset: String(offset),
      });

      if (deviceType && deviceType !== 'all') params.append('deviceType', deviceType);
      if (type && type !== 'all') params.append('type', type);

      const response = await fetch(`${PAYMENT_API_URL}plans?${params.toString()}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: token,
        },
      });

      if (checkInvalidToken(response)) return;
      if (response.status === 404) return { results: [], count: 0 };
      if (!response.ok) {
        throw new Error('Failed to fetch subscription plans');
      }
      return await response.json();
    },

    updateStatus: async (id: number | string, status: 'active' | 'inactive') => {
      const token = `Bearer ${localStorage.getItem('authToken') || ''}`;
      const response = await fetch(`${PAYMENT_API_URL}update-subs-status/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: token,
        },
        body: JSON.stringify({ status }),
      });

      if (checkInvalidToken(response)) return;
      if (!response.ok) {
        throw new Error('Failed to update subscription status');
      }
      return await response.json();
    },
  },

  // User subscriptions (revenue)
  userSubscriptions: {
    getList: async (limit?: number, offset?: number, status?: string) => {
      const token = `Bearer ${localStorage.getItem('authToken') || ''}`;

      const params = new URLSearchParams();
      if (limit !== undefined) params.append('limit', String(limit));
      if (offset !== undefined) params.append('offset', String(offset));
      if (status && status !== 'all') params.append('status', status);

      const queryString = params.toString();
      const url = `${PAYMENT_API_URL}user-subscriptions${queryString ? `?${queryString}` : ''}`;

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: token,
        },
      });

      if (checkInvalidToken(response)) return;
      if (response.status === 404) {
        return { activeCounts: 0, counts: 0, results: [], totalPlansPrice: 0 };
      }
      if (!response.ok) {
        throw new Error('Failed to fetch user subscriptions');
      }

      const data = await response.json();
      return {
        activeCounts: Number(data?.activeCounts || 0),
        counts: Number(data?.counts || 0),
        results: Array.isArray(data?.results) ? data.results : [],
        totalPlansPrice: Number(data?.totalPlansPrice || 0),
      };
    },
  },

  // Payment/Configuration endpoint
  paymentConfig: {
    addConfig: async (key: string, value: string) => {
      const token = `Bearer ${localStorage.getItem('authToken') || ''}`;
      const response = await fetch(`${PAYMENT_API_URL}config`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: token,
        },
        body: JSON.stringify({ key, value }),
      });

      if (checkInvalidToken(response)) return;

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: 'Failed to add config' }));
        throw new Error(errorData.message || 'Failed to add config');
      }

      return await response.json();
    },

    getConfig: async () => {
      const token = `Bearer ${localStorage.getItem('authToken') || ''}`;
      const response = await fetch(`${PAYMENT_API_URL}config`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: token,
        },
      });

      if (checkInvalidToken(response)) return;

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: 'Failed to get config' }));

        if (errorData?.error === 'notFound') {
          return errorData;
        }

        throw new Error(errorData.message || 'Failed to get config');
      }

      const data = await response.json();
      return data;
    },
  },
};
